import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import BoardBcm from '../components/boards/BoardBcm';
import Header from '../components/Header';
import NavigationRail, { NAV_RAIL_COLLAPSED_WIDTH } from '../components/navigation/NavigationRail';
import { createEntry, getEntryById, listEntries, listRegistryTypes } from '../api/metaRegistriesApi';
import { getDefaultBcmMap } from '../api/bcmApi';
import type { MetaRegistryEntry } from '../types/systems';
import { useTheme } from '../hooks/useTheme';
import { useProject } from '../context/ProjectContext';
import GridViewOutlinedIcon from '@mui/icons-material/GridViewOutlined';
import DnsOutlinedIcon from '@mui/icons-material/DnsOutlined';
import ExtensionOutlinedIcon from '@mui/icons-material/ExtensionOutlined';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import SchemaOutlinedIcon from '@mui/icons-material/SchemaOutlined';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import WidgetsOutlinedIcon from '@mui/icons-material/WidgetsOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';

const SLUG_ICONS: Record<string, React.ReactNode> = {
  information_systems: <DnsOutlinedIcon fontSize="small" />,
  'business-capabilities': <ExtensionOutlinedIcon fontSize="small" />,
  'bcm-maps': <MapOutlinedIcon fontSize="small" />,
};

const FALLBACK_ICONS: React.ReactNode[] = [
  <CategoryOutlinedIcon fontSize="small" />,
  <LayersOutlinedIcon fontSize="small" />,
  <HubOutlinedIcon fontSize="small" />,
  <SchemaOutlinedIcon fontSize="small" />,
  <ListAltOutlinedIcon fontSize="small" />,
  <WidgetsOutlinedIcon fontSize="small" />,
  <AssignmentOutlinedIcon fontSize="small" />,
  <AccountTreeOutlinedIcon fontSize="small" />,
];

const getRegistryIcon = (slug: string, index = 0): React.ReactNode =>
  SLUG_ICONS[slug] ?? FALLBACK_ICONS[index % FALLBACK_ICONS.length];

const BcmBoardPage: React.FC = () => {
  const { projectId, mapEntryId } = useParams<{ projectId: string; mapEntryId: string }>();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { currentProject } = useProject();
  const [railWidth, setRailWidth] = useState(NAV_RAIL_COLLAPSED_WIDTH);
  const [allRegistryTypes, setAllRegistryTypes] = useState<{ slug: string; name: string }[]>([
    { slug: 'bcm-maps', name: 'Карты бизнес-способностей' },
  ]);
  const [mapEntry, setMapEntry] = useState<MetaRegistryEntry | null>(null);
  const [maps, setMaps] = useState<MetaRegistryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [creatingMap, setCreatingMap] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    const loadMap = mapEntryId
      ? getEntryById(projectId, 'bcm-maps', mapEntryId)
      : getDefaultBcmMap(projectId);
    loadMap
      .then((entry) => setMapEntry(entry))
      .catch((e: any) => setError(e.message ?? 'load failed'))
      .finally(() => setLoading(false));
  }, [projectId, mapEntryId]);

  useEffect(() => {
    if (!projectId) return;
    listEntries(projectId, 'bcm-maps', { all: true })
      .then(setMaps)
      .catch(() => setMaps([]));
  }, [projectId, mapEntry?.id]);

  useEffect(() => {
    if (!projectId) return;
    listRegistryTypes(projectId).then(setAllRegistryTypes).catch(() => setAllRegistryTypes([]));
  }, [projectId]);

  const railItems = useMemo(() => {
    const dedicated: Record<string, string> = { information_systems: 'systems' };
    const seen = new Set<string>();
    const role = currentProject?.role;
    const items = [
      {
        id: 'diagrams',
        label: 'Диаграммы',
        icon: <GridViewOutlinedIcon fontSize="small" />,
        active: false,
        onClick: () => navigate(`/projects/${encodeURIComponent(projectId ?? '')}`),
      },
      ...allRegistryTypes
        .filter((rt) => {
          if (seen.has(rt.slug)) return false;
          seen.add(rt.slug);
          return true;
        })
        .map((rt, idx) => {
          const path = rt.slug === 'bcm-maps'
            ? `/projects/${encodeURIComponent(projectId!)}/bcm`
            : dedicated[rt.slug]
            ? `/projects/${encodeURIComponent(projectId!)}/${dedicated[rt.slug]}`
            : `/projects/${encodeURIComponent(projectId!)}/registry/${encodeURIComponent(rt.slug)}`;
          return {
            id: rt.slug,
            label: rt.name,
            icon: getRegistryIcon(rt.slug, idx),
            active: rt.slug === 'bcm-maps',
            onClick: () => navigate(path),
          };
        }),
      {
        id: 'health-map',
        label: 'Карта работоспособности',
        icon: <MonitorHeartOutlinedIcon fontSize="small" />,
        active: false,
        onClick: () => navigate(`/projects/${encodeURIComponent(projectId ?? '')}/health`),
      },
    ];
    if (role === 'owner' || role === 'admin') {
      items.push({
        id: 'settings',
        label: 'Настройки пространства',
        icon: <SettingsOutlinedIcon fontSize="small" />,
        active: false,
        onClick: () => navigate(`/projects/${encodeURIComponent(projectId!)}/settings`),
      });
    }
    return items;
  }, [allRegistryTypes, projectId, navigate, currentProject?.role]);

  const renderShell = (content: React.ReactNode) => (
    <>
      <Header title="Aspect" height={56} />
      <NavigationRail
        ariaLabel="Реестры пространства"
        theme={theme}
        items={railItems}
        onExpandedChange={setRailWidth}
      />
      <div
        style={{
          height: '100vh',
          paddingTop: 56,
          paddingLeft: railWidth,
          background: theme.background,
          transition: 'padding-left .16s ease',
          boxSizing: 'border-box',
        }}
      >
        {content}
      </div>
    </>
  );

  const handleCreateMap = async () => {
    setCreatingMap(true);
    try {
      const created = await createEntry(projectId!, 'bcm-maps', {
        code: `custom-bcm-map-${Date.now()}`,
        name: 'Новая BCM карта',
        attributes: {
          auto_populated: false,
          system_managed: false,
          root_bc_ids: [],
        },
        is_active: true,
      });
      navigate(`/projects/${projectId}/bcm/${created.id}`);
    } catch (e: any) {
      setError(e.message ?? 'create map failed');
    } finally {
      setCreatingMap(false);
    }
  };

  if (loading) {
    return renderShell(
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          background: theme.background,
          color: theme.toolbar?.text ?? '#94a3b8',
          fontSize: 16,
        }}
      >
        Загрузка карты…
      </div>
    );
  }

  if (error || !mapEntry) {
    return renderShell(
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 12,
          height: '100%',
          background: theme.background,
          color: '#f87171',
          fontSize: 16,
        }}
      >
        <span>Ошибка загрузки карты: {error}</span>
        <button onClick={() => navigate(-1)} style={{ padding: '6px 16px', cursor: 'pointer' }}>
          Назад
        </button>
      </div>
    );
  }

  return renderShell(
    <div style={{ height: '100%', background: theme.background }}>
      <BoardBcm
        projectId={projectId!}
        mapEntryId={mapEntry.id}
        mapName={mapEntry.name}
        mapAttributes={mapEntry.attributes ?? {}}
        maps={[mapEntry, ...maps.filter((item) => item.id !== mapEntry.id)]}
        selectedMapId={mapEntry.id}
        onSelectMap={(nextMapId) => {
          if (nextMapId && nextMapId !== mapEntry.id) {
            navigate(`/projects/${projectId}/bcm/${nextMapId}`);
          }
        }}
        onCreateMap={handleCreateMap}
        creatingMap={creatingMap}
      />
    </div>
  );
};

export default BcmBoardPage;
